import { FeedbackReport, User } from '@run-diffusion/shared';
import { db } from '@/firebase';

// The people a report can be assigned to on the Feedback admin page: the system admins
// (User.isAdmin), whom firestore.rules lets a claim name besides the admin making it.

// A system admin's user document, as far as the picker reads it.
export type AdminUser = Pick<User, 'id' | 'email'>;

// One admin in the picker.
export interface FeedbackAdmin {
	uid: string
	name: string // the name the page knows them by, else their email
	email: string
	isMe: boolean // the admin using the page
}

// The names the page knows people by. A users document keeps an email and no name, but a person's
// own claim on a report carries the name they work under, so the loaded reports name everyone who
// has claimed one. A claim someone was assigned carries the name the assigning admin knew, so it
// isn't read. A Map, not an object: a uid can be any string.
export const knownNames = (reports: FeedbackReport[]): Map<string, string> => {
	const names: Map<string, string> = new Map();
	reports.forEach((report: FeedbackReport) => {
		const claim = report.claim;
		if (claim?.uid && claim.kind === 'human' && !claim.assignedBy && claim.name) names.set(claim.uid, claim.name);
	});
	return names;
};

// The admins as the picker lists them: you first, then everyone else by name.
export const adminChoices = (users: AdminUser[], me: { uid: string, name: string }, names: Map<string, string>): FeedbackAdmin[] =>
	users
		.map((user: AdminUser) => {
			const isMe: boolean = user.id === me.uid;
			return {
				uid: user.id,
				name: (isMe ? me.name : names.get(user.id)) || user.email || user.id,
				email: user.email || '',
				isMe,
			};
		})
		.sort((a: FeedbackAdmin, b: FeedbackAdmin) => Number(b.isMe) - Number(a.isMe) || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

// The system admins, read once a page visit: the first picker to open reads them, and every later
// one shares that read. A read that fails is dropped, so the next picker reads again.
let pending: Promise<AdminUser[]> | null = null;

export const loadAdmins = (): Promise<AdminUser[]> => {
	if (pending) return pending;
	const read: Promise<AdminUser[]> = db.collection('users').where('isAdmin', '==', true).get()
		.then((snap) => snap.docs.map((doc) => ({ id: doc.id, email: (doc.data() as User).email || '' })));
	pending = read;
	read.catch(() => {
		if (pending === read) pending = null;
	});
	return read;
};

// Leaving the page: the next visit reads them again, so it lists anyone made an admin since.
export const forgetAdmins = (): void => {
	pending = null;
};
